import React, { lazy } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import MainLayout from '@/components/Layout/MainLayout';
import ChatPage from '@/components/pages/ChatPage';
import LoginPage from '@/components/pages/LoginPage';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEffect } from 'react';
import { firstAllowedRoute, hasPermission } from '@/utils/permissions';
import type { PagePermission } from '@/types/auth';

const ConfigPage = lazy(() => import('@/components/pages/ConfigPage'));
const SessionCenterPage = lazy(() => import('@/components/pages/SessionCenterPage'));
const KnowledgePage = lazy(() => import('@/components/pages/KnowledgePage'));
const AccessPage = lazy(() => import('@/components/pages/AccessPage'));

const FullPageLoading: React.FC = () => (
  <Box sx={{ width: '100vw', height: '100vh', display: 'grid', placeItems: 'center' }}>
    <CircularProgress />
  </Box>
);

const ProtectedLayout: React.FC = () => {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const loading = useAuthStore((s) => s.loading);
  const initialized = useAuthStore((s) => s.initialized);
  const init = useAuthStore((s) => s.init);

  useEffect(() => {
    init();
  }, [init]);

  if (!token) return <Navigate to="/login" replace />;
  if (!initialized || ((!user || !permissions) && loading)) return <FullPageLoading />;
  if (!user || !permissions) return <Navigate to="/login" replace />;
  return <MainLayout />;
};

const PermissionRoute: React.FC<{
  page: string;
  level?: PagePermission;
  children: React.ReactElement;
}> = ({ page, level = 'read', children }) => {
  const permissions = useAuthStore((s) => s.permissions);
  const loading = useAuthStore((s) => s.loading);
  if (!permissions && loading) return <FullPageLoading />;
  if (!hasPermission(permissions, page, level)) {
    return <Navigate to={firstAllowedRoute(permissions)} replace />;
  }
  return children;
};

const ForbiddenPage: React.FC = () => {
  const permissions = useAuthStore((s) => s.permissions);
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>当前账号没有可访问页面</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>请联系管理员调整角色或个人权限。</Typography>
        {firstAllowedRoute(permissions) !== '/forbidden' && (
          <Button component="a" href={firstAllowedRoute(permissions)}>返回可用页面</Button>
        )}
      </Box>
    </Box>
  );
};

const AccessRoute: React.FC = () => {
  const permissions = useAuthStore(s => s.permissions);
  if (!hasPermission(permissions, 'users') && !hasPermission(permissions, 'roles')) {
    return <Navigate to={firstAllowedRoute(permissions)} replace />;
  }
  return <AccessPage />;
};

/**
 * 应用根组件，定义全局路由和布局。
 */
const App: React.FC = () => {
  const refreshCapabilities = useAuthStore((s) => s.refreshCapabilities);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  useEffect(() => {
    void refreshCapabilities();
    const refresh = () => { void refreshCapabilities(); };
    const interval = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    window.addEventListener('deepflow:forbidden', refresh);
    window.addEventListener('deepflow:unauthorized', logout);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('deepflow:forbidden', refresh);
      window.removeEventListener('deepflow:unauthorized', logout);
    };
  }, [location.pathname, refreshCapabilities, logout]);
  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />
        <Route element={<ProtectedLayout />}>
          <Route path="/chat" element={<PermissionRoute page="chat"><ChatPage /></PermissionRoute>} />
          <Route path="/sessions" element={<PermissionRoute page="conversations"><SessionCenterPage /></PermissionRoute>} />
          <Route path="/config" element={<Navigate to="/ai-core" replace />} />
          <Route path="/ai-core" element={<PermissionRoute page="engine"><ConfigPage mode="engine" /></PermissionRoute>} />
          <Route path="/knowledge" element={<PermissionRoute page="knowledge"><KnowledgePage /></PermissionRoute>} />
          <Route path="/access" element={<AccessRoute />} />
          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Route>
      </Routes>
    </Box>
  );
};

export default App;
